import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NgModel } from '@angular/forms';

import { TranslateService } from '@ngx-translate/core';
import { of, timer } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

import { Contact, CustomSignerInput, Document, SignerMainAttributes, User, CreditTypeEnum, SecurityVerificationEnum, ActionEnum, SignerInputContext } from '@app/models';
import { AppService, DocumentService, UserService, EnterpriseService } from '@app/services';
export { HighlightPipe } from '@app/pages/documents/shared/signer-input/highlight.pipe';

@Component({
  selector: 'app-signer-input',
  templateUrl: './signer-input.component.html',
  styleUrls: ['./signer-input.component.scss'],
  standalone: false
})
export class SignerInputComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() signer: CustomSignerInput;
  @Input() document?: Document;
  @Input() qualified: boolean;
  @Input() isCountryBR: boolean;
  @Input() canShowSignerQualifiedCheckbox = true;
  @Input() validationErrors: { [k: string]: any } = {};
  @Input() isMarkdown = false;
  @Input() signerIndex: number;
  @Input() context = SignerInputContext.Default;
  @Output() changeSigner = new EventEmitter<CustomSignerInput>();
  @Output() removeSigner = new EventEmitter<number>();
  @ViewChild('inputModel') inputModel: NgModel;
  @ViewChild('phoneInputModel') phoneInputModel: NgModel;
  @ViewChild('inputElement') inputElement: ElementRef<HTMLInputElement>;
  @ViewChild('suggestions') suggestionsRef: ElementRef<HTMLDivElement>;
  searchQuery: string;
  currentUser: User;
  isAutentiqueUser = false;
  contacts: Contact[] = [];
  hasPhoneError = false;
  hasVerificationPhoneError = false;
  cpfCnpj = '';
  isEditOptionsDisabled: boolean;
  tooltipPlacement: 'right' | 'left' = 'right';

  readonly CreditTypeEnum = CreditTypeEnum;
  readonly SecurityVerificationEnum = SecurityVerificationEnum;
  readonly inputId = 'signer-input-element-' + Math.floor(Math.random() * 1000000);
  readonly SignerMainAttributes = SignerMainAttributes;
  readonly signerMainAttributesValues = Object.values(SignerMainAttributes);
  readonly ActionEnum = ActionEnum;
  readonly SignerInputContext = SignerInputContext;

  constructor(
    public appService: AppService,
    public documentService: DocumentService,
    public translateService: TranslateService,
    public enterpriseService: EnterpriseService,
    private userService: UserService
  ) {}

  ngAfterViewInit() {
    if (this.signer._extra.signerMainAttribute) {
      setTimeout(() => {
        if (this.inputModel && this.inputModel.value) {
          this.inputModel.control.markAsTouched();
        } else if (this.phoneInputModel && this.phoneInputModel.value) {
          this.phoneInputModel.control.markAsTouched();
        }

        this.setCpfCnpj();
      });
    }
  }

  ngOnInit() {
    this.userService
      .getCurrentUser()
      .pipe(filter(user => !!user))
      .subscribe({
        next: user => {
          this.currentUser = user;
          this.isAutentiqueUser = !!(user.email || '').match(/@autentique\.com\.br$/);
        }
      });

    if (this.context === SignerInputContext.Editor) {
      this.tooltipPlacement = 'left';
    }

    this.setEditOptionsUneditable();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.canShowSignerQualifiedCheckbox?.currentValue === false && this.signer?._extra?.isQualified) {
      this.signer._extra.isQualified = false;
      this.changeSigner.emit(this.signer);
    }

    this.setCpfCnpj();

    if (this.isEditOptionsDisabled !== undefined && changes.signer?.currentValue?._extra.isEditable === this.isEditOptionsDisabled) {
      this.setEditOptionsUneditable();
    }
  }

  modelChange(signer: CustomSignerInput) {
    this.changeSigner.emit(signer);

    const attr = signer._extra?.signerMainAttribute;
    if (!attr || attr === 'name') return;

    const isPhone = ['whatsapp', 'sms'].includes(attr);
    const minLength = isPhone ? 7 : 3;
    const value = signer[attr];

    if (!value || value.length < minLength) return;
    if (attr === 'email' && value.startsWith('+')) return;

    this.searchContacts(signer);
  }

  searchContacts(signer: CustomSignerInput) {
    const attr = signer._extra?.signerMainAttribute;
    const isPhoneAttr = ['whatsapp', 'sms'].includes(attr);
    const parseSearchQuery = () => ((signer && signer[attr]) || '').trim().slice(0, 30).toLowerCase();
    const search = parseSearchQuery();
    const currentUserValue = isPhoneAttr ? this.currentUser?.phone : this.currentUser?.email;

    if (this.currentUser && search && search !== currentUserValue) {
      timer(500)
        .pipe(
          switchMap(() => (search === parseSearchQuery() ? this.userService.contacts({ search, limit: 5 }) : of(null))),
          map(contacts => (contacts || []).filter((contact: Contact) => !!contact.name && !!contact[isPhoneAttr ? 'phone' : 'email'])),
          filter(contacts => contacts?.length > 0)
        )
        .subscribe({
          next: contacts => (this.contacts = contacts)
        });
    } else {
      this.contacts = [];
    }
  }

  getContactDetail(contact: Contact, signer: CustomSignerInput): string {
    const attr = signer._extra.signerMainAttribute;
    return ['whatsapp', 'sms'].includes(attr) ? contact.phone : contact.email;
  }

  selectContact(contact: Contact) {
    const attr = this.signer._extra.signerMainAttribute;

    if (['whatsapp', 'sms'].includes(attr)) {
      this.signer[attr] = contact.phone;
    } else {
      this.signer[attr] = contact.email;
    }

    this.changeSigner.emit(this.signer);
    this.contacts = [];
    this.inputElement?.nativeElement.focus();
  }

  changeSignerMainAttribute(signer: CustomSignerInput, attr: SignerMainAttributes) {
    if (signer._extra.signerMainAttribute !== attr) {
      const lastMainAttribute = this.signerMainAttributesValues.find(mainAttr => mainAttr === signer._extra.signerMainAttribute);
      if ([`${SignerMainAttributes.Sms}`, `${SignerMainAttributes.Whatsapp}`].includes(attr) && [`${SignerMainAttributes.Sms}`, `${SignerMainAttributes.Whatsapp}`].includes(lastMainAttribute)) {
        signer[attr] = signer[lastMainAttribute] || '';
      } else {
        signer[attr] = '';
      }
      delete signer[lastMainAttribute];
      signer._extra.signerMainAttribute = attr as SignerMainAttributes;

      setTimeout(() => {
        if (['email', 'name'].includes(this.signer._extra.signerMainAttribute)) {
          this.inputModel.control.markAsTouched();
          this.inputModel.control.updateValueAndValidity();
        } else if (['whatsapp', 'sms'].includes(this.signer._extra.signerMainAttribute)) {
          this.phoneInputModel.control.markAsTouched();
          this.phoneInputModel.control.updateValueAndValidity();
        }
      });
    }

    this.changeSigner.emit(signer);
  }

  getInputPlaceholder(): string {
    let placeholder: string = 'documents.';

    if (this.signerIndex === 0) {
      placeholder += 'creator';
    } else if (this.signer._extra.isEditable || this.context !== SignerInputContext.Editor) {
      if (this.signer._extra.signerMainAttribute === 'name') {
        placeholder += 'signatoryName';
      } else if (this.signer._extra.signerMainAttribute === 'email') {
        placeholder += 'signatoryEmail';
      } else {
        placeholder += 'signatoryPhone';
      }
    } else {
      placeholder += 'signatorie';
    }

    placeholder = this.translateService.instant(placeholder);
    return placeholder + (this.signerIndex > 0 ? ' ' + this.signerIndex : '');
  }

  shouldShowQualifiedOption(): boolean {
    return (
      this.canShowSignerQualifiedCheckbox &&
      !this.qualified &&
      !this.document?.is_dynamic_document &&
      !this.isMarkdown &&
      this.context !== SignerInputContext.Editor &&
      !this.enterpriseService.hideForEnterprise()
    );
  }

  changeSignerAdvancedOption() {
    if (this.context === SignerInputContext.Default) {
      this.signer._extra.isLocked = !this.signer._extra.isLocked;
    } else {
      this.signer._extra.isEditable = !this.signer._extra.isEditable;
    }

    this.changeSigner.emit(this.signer);
    this.setEditOptionsUneditable();
  }

  setEditOptionsUneditable() {
    this.isEditOptionsDisabled = (this.signerIndex === 0 || (!this.signer._extra.isEditable && this.context !== SignerInputContext.Default)) && this.context !== SignerInputContext.CreateSigner;
  }

  onCpfCnpjChange(signer: CustomSignerInput): void {
    const sanitized = this.cpfCnpj ? this.cpfCnpj.replace(/\D/g, '') : '';
    const isCnpj = sanitized.length > 11;

    if (signer.configs?.cpf !== undefined) {
      delete signer.configs.cpf;
    }
    if (signer.configs?.cnpj !== undefined) {
      delete signer.configs.cnpj;
    }

    if (this.cpfCnpj) {
      if (!signer.configs) {
        signer.configs = {};
      }

      if (isCnpj) {
        signer.configs.cnpj = this.cpfCnpj;
      } else {
        signer.configs.cpf = this.cpfCnpj;
      }
    }

    this.changeSigner.emit(signer);
  }

  onSignerQualifiedChange() {
    if (!this.signer._extra) {
      this.signer._extra = {} as any;
    }

    this.signer._extra.isQualified = !this.signer._extra.isQualified;

    if (this.signer._extra.isQualified) {
      this.signer._extra.showSmsVerification = false;
      this.signer._extra.showSecurityVerifications = false;
      // this.signer._extra.showCpfVerification = false;
    }

    this.setCpfCnpj();

    this.changeSigner.emit(this.signer);
  }

  hidePfFacialMatch(): boolean {
    const isWrongCountry = !this.isCountryBR;
    const isInvalidDomain = !this.appService.isAutentiqueDomain();
    const isWrongOrg = this.currentUser.organization.id !== 18845275; // Esconde a opção para todas as organizações, exceto para a METHA

    if (this.isAutentiqueUser) {
      return isWrongCountry;
    }

    return isWrongCountry || isInvalidDomain || isWrongOrg;
  }

  private setCpfCnpj() {
    const configs = this.signer?.configs;

    if (!this.qualified && !this.signer?._extra?.isQualified) {
      delete configs?.cnpj;
    }

    this.cpfCnpj = configs?.cnpj || configs?.cpf || '';
  }

  @HostListener('keydown', ['$event']) onKeyDown(event: KeyboardEvent) {
    if (this.contacts && this.contacts.length > 0) {
      const key = (event.key || '').toLowerCase();
      if (key === 'escape') {
        this.contacts = [];
      } else if (['arrowup', 'arrowdown'].includes(key)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const element = (key === 'arrowup' ? document.activeElement.previousElementSibling : document.activeElement.nextElementSibling) as HTMLAnchorElement;
        if (element && element.tagName === 'A' && this.suggestionsRef.nativeElement.contains(element)) {
          element.focus();
        } else {
          (this.suggestionsRef.nativeElement.querySelector('a:' + (key === 'arrowup' ? 'last-child' : 'first-child')) as HTMLAnchorElement).focus();
        }
      }
    }
  }

  @HostListener('focusout', ['$event']) onBlur(event: FocusEvent) {
    setTimeout(() => {
      if (!event.relatedTarget || !this.suggestionsRef.nativeElement.contains(event.relatedTarget as HTMLElement)) {
        this.contacts = [];
      }
    }, 200);
  }
}
